import { useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { inviteUser } from '@/api/users'
import { useApiErrorMessage } from '@/api/errors'
import { Role } from '@/auth/roles'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field } from '@/components/layout/Field'
import { FieldGrid } from '@/components/layout/FormSection'
import { Input } from '@/components/ui/input'
import { RoleSelect } from '@/components/users/RoleSelect'
import { isEmail } from '@/components/users/validation'

type Errors = { email?: string; firstName?: string; lastName?: string }

export function InviteUserDialog({
  open,
  onOpenChange,
  onInvited,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The list is stale afterwards either way — a failed mail still created the account. */
  onInvited: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* The form is mounted by opening the dialog and thrown away by closing it, so it always
            starts empty. Resetting the fields by hand is the kind of thing that gets forgotten
            after a failed submit — and then invites the wrong person. */}
        {open && <InviteForm onOpenChange={onOpenChange} onInvited={onInvited} />}
      </DialogContent>
    </Dialog>
  )
}

function InviteForm({
  onOpenChange,
  onInvited,
}: {
  onOpenChange: (open: boolean) => void
  onInvited: () => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()

  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [role, setRole] = useState<Role>(Role.SALES)
  const [errors, setErrors] = useState<Errors>({})
  const [failure, setFailure] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  function validate(): boolean {
    const next: Errors = {}
    if (!email.trim()) {
      next.email = t('validation.required')
    } else if (!isEmail(email)) {
      next.email = t('validation.email')
    }
    if (!firstName.trim()) {
      next.firstName = t('validation.required')
    }
    if (!lastName.trim()) {
      next.lastName = t('validation.required')
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitting || !validate()) {
      return
    }
    setSubmitting(true)
    setFailure(null)
    try {
      const address = email.trim()
      const name = `${firstName.trim()} ${lastName.trim()}`.trim()
      const result = await inviteUser({
        email: address,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role,
      })
      // The account exists whether or not the mail went out, and the difference decides what the
      // administrator has to do next — so it is a warning, not a quiet success.
      if (result.invitationSent) {
        toast.success(t('users.invite.success', { email: address }))
      } else {
        toast.warning(t('users.invite.successWithoutMail', { name }), { duration: 12_000 })
      }
      onInvited()
      onOpenChange(false)
    } catch (error) {
      // Kept inside the dialog rather than thrown into a toast: the form is still on screen and
      // the address that was refused is the thing to change.
      setFailure(errorMessage(error, t('users.invite.failed')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <DialogHeader>
        <DialogTitle>{t('users.invite.title')}</DialogTitle>
        <DialogDescription>{t('users.invite.description')}</DialogDescription>
      </DialogHeader>

      <div className="grid gap-4 py-6">
        {failure && (
          <Alert variant="destructive">
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}

        <Field id="invite-email" label={t('users.invite.email')} error={errors.email}>
          <Input
            id="invite-email"
            type="email"
            autoComplete="off"
            value={email}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'invite-email-error' : undefined}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <FieldGrid>
          <Field id="invite-first-name" label={t('users.invite.firstName')} error={errors.firstName}>
            <Input
              id="invite-first-name"
              value={firstName}
              aria-invalid={Boolean(errors.firstName)}
              aria-describedby={errors.firstName ? 'invite-first-name-error' : undefined}
              onChange={(event) => setFirstName(event.target.value)}
            />
          </Field>
          <Field id="invite-last-name" label={t('users.invite.lastName')} error={errors.lastName}>
            <Input
              id="invite-last-name"
              value={lastName}
              aria-invalid={Boolean(errors.lastName)}
              aria-describedby={errors.lastName ? 'invite-last-name-error' : undefined}
              onChange={(event) => setLastName(event.target.value)}
            />
          </Field>
        </FieldGrid>

        <Field id="invite-role" label={t('users.invite.role')}>
          <RoleSelect id="invite-role" value={role} onChange={setRole} />
        </Field>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={submitting}>
          {t('users.invite.submit')}
        </Button>
      </DialogFooter>
    </form>
  )
}
